<script>
    import { theme } from "$lib/utils/theme.svelte.ts";
</script>

<div
    class="celestial-body"
    class:dark={theme.resolved === "dark"}
    aria-hidden="true"
>
    <!-- Sun (light mode) -->
    <div class="celestial-sun">
        <div class="sun-disc"></div>
        <div class="sun-aura"></div>
    </div>

    <!-- Moon (dark mode) -->
    <div class="celestial-moon">
        <div class="moon-disc">
            <div class="moon-crater moon-crater--1"></div>
            <div class="moon-crater moon-crater--2"></div>
            <div class="moon-crater moon-crater--3"></div>
            <div class="moon-crater moon-crater--4"></div>
            <div class="moon-crater moon-crater--5"></div>
            <div class="moon-crater moon-crater--6"></div>
            <div class="moon-crater moon-crater--7"></div>
        </div>
        <div class="moon-aura"></div>
    </div>
</div>

<style>
    .celestial-body {
        position: absolute;
        inset: 0;
        overflow: visible;
        pointer-events: none;
        z-index: 0;
        --sun-scale-start: 4.2;
        --sun-scale-end: 1;
        --moon-scale-start: 3;
        --moon-scale-end: 0.69;
    }

    /* ═══════════════════════════════════
       SUN
       ═══════════════════════════════════ */
    .celestial-sun {
        position: absolute;
        top: 12%;
        left: 50%;
        transform: translate(-50%, -50%) scale(var(--sun-scale-start));
        /* The disc size — aura extends beyond this */
        width: 12vw;
        height: 12vw;
        opacity: 1;
        transition: opacity 0.6s ease;
    }
    .dark .celestial-sun {
        opacity: 0;
    }

    /* Solid photosphere disc */
    .sun-disc {
        position: absolute;
        inset: 0;
        border-radius: 50%;
        background: radial-gradient(
            circle at 40% 38%,
            #fff 0%,
            #fffde8 12%,
            #ffe566 30%,
            #ffcc00 50%,
            #ffaa00 70%,
            #ff8c00 88%,
            #e67300 100%
        );
        box-shadow:
            inset -1.5vw -0.5vw 2vw -0.5vw rgba(200, 100, 0, 0.3),
            inset 0.8vw 0.5vw 1.5vw -0.3vw rgba(255, 255, 230, 0.6);
        z-index: 2;
    }

    /* Aura — static box-shadow, opacity pulses to avoid repaint-per-frame */
    .sun-aura {
        position: absolute;
        inset: 0;
        border-radius: 50%;
        background: transparent;
        z-index: 1;
        box-shadow:
            0 0 15px 8px rgba(255, 230, 100, 0.9),
            0 0 50px 25px rgba(255, 210, 60, 0.65),
            0 0 110px 55px rgba(255, 180, 30, 0.4),
            0 0 220px 110px rgba(255, 150, 0, 0.22),
            0 0 380px 190px rgba(255, 120, 0, 0.1),
            0 0 550px 275px rgba(255, 100, 0, 0.04);
        animation: sun-pulse 4s ease-in-out infinite alternate;
    }

    @keyframes sun-pulse {
        from { opacity: 0.45; }
        to { opacity: 1; }
    }

    /* ═══════════════════════════════════
       MOON
       ═══════════════════════════════════ */
    .celestial-moon {
        position: absolute;
        top: 12%;
        left: 50%;
        transform: translate(-50%, -50%) scale(var(--moon-scale-start));
        width: 10vw;
        height: 10vw;
        opacity: 0;
        transition: opacity 0.6s ease;
    }
    .dark .celestial-moon {
        opacity: 1;
    }

    /* Solid moon disc — realistic lit sphere with terminator */
    .moon-disc {
        position: absolute;
        inset: 0;
        border-radius: 50%;
        background: radial-gradient(
            circle at 38% 35%,
            #f8f8fc 0%,
            #efeff4 15%,
            #e0e0ea 30%,
            #d0d0dc 45%,
            #bbbbc8 60%,
            #a5a5b5 75%,
            #9090a2 90%,
            #7e7e92 100%
        );
        box-shadow:
            /* terminator — shadow on the right/bottom edge */
            inset -2.5vw 0.5vw 3vw -1vw rgba(15, 15, 35, 0.55),
            /* lit-edge highlight — upper-left */ inset 1vw -0.5vw 1.5vw -0.3vw
                rgba(255, 255, 255, 0.35);
        z-index: 2;
        overflow: hidden;
    }

    /* Craters — subtle depressions with rim highlights */
    .moon-crater {
        position: absolute;
        border-radius: 50%;
        background: radial-gradient(
            circle at 38% 32%,
            rgba(145, 145, 165, 0.25) 0%,
            rgba(115, 115, 140, 0.5) 40%,
            rgba(95, 95, 120, 0.35) 70%,
            rgba(130, 130, 155, 0.15) 100%
        );
        box-shadow:
            inset 0.5px 0.5px 1.5px rgba(255, 255, 255, 0.2),
            inset -0.5px -0.5px 1.5px rgba(0, 0, 20, 0.25);
    }
    /* Large mare-like regions */
    .moon-crater--1 {
        width: 30%;
        height: 30%;
        top: 15%;
        left: 20%;
        opacity: 0.7;
    }
    .moon-crater--2 {
        width: 22%;
        height: 22%;
        top: 50%;
        left: 55%;
    }
    .moon-crater--3 {
        width: 35%;
        height: 30%;
        top: 35%;
        left: 8%;
        opacity: 0.5;
    }
    /* Smaller impact craters */
    .moon-crater--4 {
        width: 14%;
        height: 14%;
        top: 22%;
        left: 58%;
    }
    .moon-crater--5 {
        width: 18%;
        height: 18%;
        top: 62%;
        left: 30%;
    }
    .moon-crater--6 {
        width: 10%;
        height: 10%;
        top: 10%;
        left: 45%;
    }
    .moon-crater--7 {
        width: 8%;
        height: 8%;
        top: 75%;
        left: 65%;
        opacity: 0.6;
    }

    /* Aura — static box-shadow, opacity pulses to avoid repaint-per-frame */
    .moon-aura {
        position: absolute;
        inset: 0;
        border-radius: 50%;
        background: transparent;
        z-index: 1;
        box-shadow:
            0 0 12px 6px rgba(210, 225, 250, 0.8),
            0 0 40px 20px rgba(190, 210, 245, 0.5),
            0 0 90px 45px rgba(170, 195, 235, 0.3),
            0 0 180px 90px rgba(150, 180, 225, 0.15),
            0 0 300px 150px rgba(130, 160, 215, 0.06);
        animation: moon-pulse 5s ease-in-out infinite alternate;
    }

    @keyframes moon-pulse {
        from { opacity: 0.4; }
        to { opacity: 1; }
    }

    @keyframes celestial-sun-depth {
        from {
            transform: translate(-50%, -50%) scale(var(--sun-scale-start));
        }
        to {
            transform: translate(-50%, -50%) scale(var(--sun-scale-end));
        }
    }

    @keyframes celestial-moon-depth {
        from {
            transform: translate(-50%, -50%) scale(var(--moon-scale-start));
        }
        to {
            transform: translate(-50%, -50%) scale(var(--moon-scale-end));
        }
    }

    @supports (animation-timeline: view()) {
        .celestial-sun {
            animation: celestial-sun-depth linear forwards;
            animation-timeline: view();
            animation-range: entry 0% exit 100%;
        }

        .celestial-moon {
            animation: celestial-moon-depth linear forwards;
            animation-timeline: view();
            animation-range: entry 0% exit 100%;
        }
    }

    /* ─── Reduced motion ─── */
    @media (prefers-reduced-motion: reduce) {
        .sun-aura,
        .moon-aura {
            animation: none;
            opacity: 0.7;
        }
    }
</style>
